/**
 * InteractionEngine.js
 * Runtime engine for processing form field interactions defined in formConfig.interactions[].
 *
 * Supported interaction types:
 *   - cascade:            Refresh child dropdown options when parent changes
 *   - visibility:         Show/hide fields based on trigger field value
 *   - computed:           Auto-calculate field value from an expression over other fields
 *   - auto_fill:          Fetch data from API and populate target fields
 *   - conditional_enable: Enable/disable fields based on trigger field value
 *
 * Usage:
 *   const engine = new InteractionEngine(interactions, {
 *     setFieldValue, setFieldOptions, setFieldVisibility, setFieldDisabled,
 *     getFieldValue, dataSourceManager
 *   });
 *   // Call on every field change:
 *   engine.onFieldChange("company_id", "5", allFormValues);
 *   // Cleanup:
 *   engine.destroy();
 */

import configData from "../../config.json";

const MAX_CASCADE_DEPTH = 5;

class InteractionEngine {
  /**
   * @param {Array} interactions - interactions[] from formConfig
   * @param {Object} callbacks
   * @param {Function} callbacks.setFieldValue      - (fieldName, value) => void
   * @param {Function} callbacks.setFieldOptions     - (fieldName, options[]) => void
   * @param {Function} callbacks.setFieldVisibility  - (fieldName, visible) => void
   * @param {Function} callbacks.setFieldDisabled    - (fieldName, disabled) => void
   * @param {Function} callbacks.getFieldValue       - (fieldName) => value
   * @param {Object}   callbacks.dataSourceManager   - DataSourceManager instance (optional, for cascade)
   * @param {string}   sId - Session ID for API calls
   * @param {string}   uId - User ID for API calls
   */
  constructor(interactions, callbacks, sId, uId) {
    this.interactions = interactions || [];
    this.callbacks = callbacks || {};
    this.sId = sId || sessionStorage.getItem("sessionId");
    this.uId = uId || sessionStorage.getItem("uId");

    // Track cascade depth to prevent infinite loops
    this._cascadeDepth = 0;

    // Build a lookup: { fieldName: [interaction, ...] } for fast trigger matching
    this._triggerMap = {};
    this.interactions.forEach((interaction) => {
      const triggerField = interaction.trigger && interaction.trigger.field;
      if (triggerField) {
        if (!this._triggerMap[triggerField]) {
          this._triggerMap[triggerField] = [];
        }
        this._triggerMap[triggerField].push(interaction);
      }
    });
  }

  /**
   * Called whenever a form field value changes.
   * Finds all interactions triggered by this field and executes them.
   *
   * @param {string} fieldName - The field that changed
   * @param {*} value - The new value
   * @param {Object} allValues - All current form values { fieldName: value }
   */
  onFieldChange(fieldName, value, allValues) {
    const triggered = this._triggerMap[fieldName];
    if (!triggered || triggered.length === 0) return;

    triggered.forEach((interaction) => {
      const event = interaction.trigger.event || "change";
      // Currently only "change" event is supported at runtime
      if (event !== "change") return;

      try {
        switch (interaction.type) {
          case "cascade":
            this._handleCascade(interaction, fieldName, value, allValues);
            break;
          case "visibility":
            this._handleVisibility(interaction, fieldName, value, allValues);
            break;
          case "computed":
            this._handleComputed(interaction, fieldName, value, allValues);
            break;
          case "auto_fill":
            this._handleAutoFill(interaction, fieldName, value, allValues);
            break;
          case "conditional_enable":
            this._handleConditionalEnable(interaction, fieldName, value, allValues);
            break;
          default:
            console.warn(`InteractionEngine: Unknown interaction type "${interaction.type}"`);
        }
      } catch (err) {
        console.error(`InteractionEngine: Error processing "${interaction.type}" for field "${fieldName}":`, err);
      }
    });
  }

  /**
   * Evaluate all interactions for initial state (e.g., on form load with pre-filled values).
   * Useful when editing an existing record — visibility/enable rules need to be applied immediately.
   *
   * @param {Object} allValues - All current form values
   */
  evaluateAll(allValues) {
    this.interactions.forEach((interaction) => {
      const triggerField = interaction.trigger && interaction.trigger.field;
      if (!triggerField) return;
      const value = allValues[triggerField];

      try {
        switch (interaction.type) {
          case "visibility":
            this._handleVisibility(interaction, triggerField, value, allValues);
            break;
          case "conditional_enable":
            this._handleConditionalEnable(interaction, triggerField, value, allValues);
            break;
          case "computed":
            this._handleComputed(interaction, triggerField, value, allValues);
            break;
          default:
            // cascade and auto_fill not re-evaluated on load — they need explicit trigger
            break;
        }
      } catch (err) {
        console.error(`InteractionEngine: Error in evaluateAll for "${interaction.type}":`, err);
      }
    });
  }

  // ─── Interaction Handlers ──────────────────────────────────────────────

  /**
   * Cascade: When parent changes, fetch new options for child field.
   * Uses DataSourceManager if available, otherwise fetches directly.
   *
   * Config shape:
   * {
   *   type: "cascade",
   *   trigger: { field: "company_id", event: "change" },
   *   target: { field: "employee_id" },
   *   action: {
   *     endpoint: "C_Employee/getByCompany",
   *     paramField: "company_id",
   *     labelField: "name",
   *     valueField: "id",
   *     dataSourceId: "ds_employees"  // optional: use existing dataSource
   *   }
   * }
   */
  _handleCascade(interaction, fieldName, value, allValues) {
    if (this._cascadeDepth >= MAX_CASCADE_DEPTH) {
      console.warn("InteractionEngine: Max cascade depth reached, stopping to prevent infinite loop.");
      return;
    }

    const targetField = interaction.target && interaction.target.field;
    const action = interaction.action || {};
    if (!targetField) return;

    // Clear target field value
    if (this.callbacks.setFieldValue) {
      this.callbacks.setFieldValue(targetField, "");
    }

    if (!value) {
      // Parent cleared — clear child options
      if (this.callbacks.setFieldOptions) {
        this.callbacks.setFieldOptions(targetField, []);
      }
      return;
    }

    this._cascadeDepth++;

    // If a dataSourceId is specified and DataSourceManager is available, delegate to it
    if (action.dataSourceId && this.callbacks.dataSourceManager) {
      const paramField = action.paramField || fieldName;
      this.callbacks.dataSourceManager
        .fetchOptions(action.dataSourceId, { [paramField]: value })
        .then((options) => {
          if (this.callbacks.setFieldOptions) {
            this.callbacks.setFieldOptions(targetField, options);
          }
          this._cascadeDepth--;
          // Trigger downstream cascades on the target field (now cleared)
          this.onFieldChange(targetField, "", { ...allValues, [targetField]: "" });
        })
        .catch(() => {
          this._cascadeDepth--;
        });
      return;
    }

    // Direct API fetch
    if (!action.endpoint) {
      this._cascadeDepth--;
      return;
    }

    const paramField = action.paramField || fieldName;
    const formData = new FormData();
    formData.append("sId", this.sId);
    formData.append("uId", this.uId);
    formData.append(paramField, value);

    fetch(configData.api_url + action.endpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        const list = data.list || data.data || data.options || [];
        const labelField = action.labelField || "name";
        const valueField = action.valueField || "id";

        const options = Array.isArray(list)
          ? list.map((item) => ({
              value: String(item[valueField] ?? ""),
              label: String(item[labelField] ?? item[valueField] ?? ""),
              _raw: item,
            }))
          : [];

        if (this.callbacks.setFieldOptions) {
          this.callbacks.setFieldOptions(targetField, options);
        }
        this._cascadeDepth--;
        // Trigger downstream cascades
        this.onFieldChange(targetField, "", { ...allValues, [targetField]: "" });
      })
      .catch((err) => {
        console.error(`InteractionEngine: Cascade fetch error for "${targetField}":`, err);
        this._cascadeDepth--;
      });
  }

  /**
   * Visibility: Show/hide target field(s) based on condition.
   *
   * Config shape:
   * {
   *   type: "visibility",
   *   trigger: { field: "employment_type", event: "change" },
   *   target: { field: "contract_end_date" } | { fields: ["field_a", "field_b"] },
   *   action: {
   *     condition: "equals",       // equals | not_equals | in | not_in | not_empty | empty | greater_than | less_than
   *     value: "contract",         // single value or array for "in"/"not_in"
   *     show: true                 // true = show when condition met, false = hide when condition met
   *   }
   * }
   */
  _handleVisibility(interaction, fieldName, value, allValues) {
    const targets = this._getTargetFields(interaction);
    const action = interaction.action || {};
    const conditionMet = this._evaluateCondition(action.condition, value, action.value, allValues);
    const shouldShow = action.show !== false ? conditionMet : !conditionMet;

    targets.forEach((target) => {
      if (this.callbacks.setFieldVisibility) {
        this.callbacks.setFieldVisibility(target, shouldShow);
      }
    });
  }

  /**
   * Computed: Calculate a value from an expression using other field values.
   *
   * Config shape:
   * {
   *   type: "computed",
   *   trigger: { field: "height", event: "change" },
   *   target: { field: "bmi" },
   *   action: {
   *     expression: "weight / ((height / 100) * (height / 100))",
   *     fields: ["weight", "height"],     // fields referenced in expression
   *     round: 2                          // optional: decimal places to round to
   *   }
   * }
   */
  _handleComputed(interaction, fieldName, value, allValues) {
    const targetField = interaction.target && interaction.target.field;
    const action = interaction.action || {};
    if (!targetField || !action.expression) return;

    const referencedFields = action.fields || [];
    const fieldValues = {};
    referencedFields.forEach((f) => {
      const val = allValues[f];
      fieldValues[f] = val !== undefined && val !== "" ? Number(val) : 0;
    });

    // Also include the just-changed field with its new value
    if (value !== undefined && value !== "") {
      fieldValues[fieldName] = Number(value);
    }

    const result = this._safeEvaluate(action.expression, fieldValues);

    if (result !== null && !isNaN(result) && isFinite(result)) {
      const rounded = action.round !== undefined
        ? Number(result.toFixed(action.round))
        : result;

      if (this.callbacks.setFieldValue) {
        this.callbacks.setFieldValue(targetField, String(rounded));
      }
    }
  }

  /**
   * Auto-fill: Fetch data from API using trigger value and populate multiple target fields.
   *
   * Config shape:
   * {
   *   type: "auto_fill",
   *   trigger: { field: "employee_id", event: "change" },
   *   target: { fields: ["employee_name", "employee_email", "department"] },
   *   action: {
   *     endpoint: "C_Employee/getDetails",
   *     paramField: "employee_id",
   *     fieldMapping: {
   *       "employee_name": "name",       // target_field: api_response_field
   *       "employee_email": "email",
   *       "department": "dept_name"
   *     }
   *   }
   * }
   */
  _handleAutoFill(interaction, fieldName, value, allValues) {
    const action = interaction.action || {};
    if (!action.endpoint || !action.fieldMapping) return;

    if (!value) {
      // Clear all auto-fill targets
      Object.keys(action.fieldMapping).forEach((targetField) => {
        if (this.callbacks.setFieldValue) {
          this.callbacks.setFieldValue(targetField, "");
        }
      });
      return;
    }

    const paramField = action.paramField || fieldName;
    const formData = new FormData();
    formData.append("sId", this.sId);
    formData.append("uId", this.uId);
    formData.append(paramField, value);

    fetch(configData.api_url + action.endpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        // Extract the record — could be in data.data, data.record, data.list[0], or data itself
        const record = data.record || data.data || (Array.isArray(data.list) ? data.list[0] : null) || data;

        if (record && typeof record === "object") {
          Object.entries(action.fieldMapping).forEach(([targetField, sourceField]) => {
            const val = record[sourceField];
            if (val !== undefined && this.callbacks.setFieldValue) {
              this.callbacks.setFieldValue(targetField, String(val));
            }
          });
        }
      })
      .catch((err) => {
        console.error(`InteractionEngine: Auto-fill fetch error:`, err);
      });
  }

  /**
   * Conditional Enable: Enable/disable target field(s) based on condition.
   *
   * Config shape:
   * {
   *   type: "conditional_enable",
   *   trigger: { field: "has_insurance", event: "change" },
   *   target: { field: "policy_number" } | { fields: ["policy_number", "insurer"] },
   *   action: {
   *     condition: "equals",
   *     value: "yes",
   *     enable: true           // true = enable when condition met, false = disable when condition met
   *   }
   * }
   */
  _handleConditionalEnable(interaction, fieldName, value, allValues) {
    const targets = this._getTargetFields(interaction);
    const action = interaction.action || {};
    const conditionMet = this._evaluateCondition(action.condition, value, action.value, allValues);
    const shouldEnable = action.enable !== false ? conditionMet : !conditionMet;

    targets.forEach((target) => {
      if (this.callbacks.setFieldDisabled) {
        this.callbacks.setFieldDisabled(target, !shouldEnable);
      }
    });
  }

  // ─── Helper Methods ────────────────────────────────────────────────────

  /**
   * Get target field(s) from interaction config.
   * Supports both { field: "name" } and { fields: ["a", "b"] }.
   */
  _getTargetFields(interaction) {
    const target = interaction.target || {};
    if (target.fields && Array.isArray(target.fields)) {
      return target.fields;
    }
    if (target.field) {
      return [target.field];
    }
    return [];
  }

  /**
   * Evaluate a condition against a field value.
   *
   * @param {string} condition - Condition type
   * @param {*} fieldValue - Current field value
   * @param {*} compareValue - Value to compare against (from config)
   * @param {Object} allValues - All form values (for advanced conditions)
   * @returns {boolean}
   */
  _evaluateCondition(condition, fieldValue, compareValue, allValues) {
    const strValue = String(fieldValue ?? "");

    switch (condition) {
      case "equals":
        return strValue === String(compareValue ?? "");

      case "not_equals":
        return strValue !== String(compareValue ?? "");

      case "in":
        if (Array.isArray(compareValue)) {
          return compareValue.map(String).includes(strValue);
        }
        return false;

      case "not_in":
        if (Array.isArray(compareValue)) {
          return !compareValue.map(String).includes(strValue);
        }
        return true;

      case "not_empty":
        return strValue !== "" && strValue !== "null" && strValue !== "undefined";

      case "empty":
        return strValue === "" || strValue === "null" || strValue === "undefined";

      case "greater_than":
        return Number(fieldValue) > Number(compareValue);

      case "less_than":
        return Number(fieldValue) < Number(compareValue);

      case "contains":
        return strValue.toLowerCase().includes(String(compareValue ?? "").toLowerCase());

      case "starts_with":
        return strValue.toLowerCase().startsWith(String(compareValue ?? "").toLowerCase());

      default:
        // Default: treat as "not_empty" (any truthy value triggers)
        return strValue !== "";
    }
  }

  /**
   * Safely evaluate a mathematical expression with field values.
   * Only allows arithmetic operators and field references — no DOM/global access.
   *
   * @param {string} expression - e.g. "weight / ((height / 100) * (height / 100))"
   * @param {Object} fieldValues - { fieldName: numericValue }
   * @returns {number|null}
   */
  _safeEvaluate(expression, fieldValues) {
    try {
      // Replace field names with their values in the expression
      let safeExpr = expression;
      // Sort by length descending to avoid partial replacements (e.g. "height" before "h")
      const fieldNames = Object.keys(fieldValues).sort((a, b) => b.length - a.length);

      fieldNames.forEach((name) => {
        // Use word boundary regex to replace field names
        const regex = new RegExp("\\b" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b", "g");
        safeExpr = safeExpr.replace(regex, String(fieldValues[name]));
      });

      // Validate: only allow numbers, arithmetic operators, parentheses, spaces, decimal points
      if (!/^[\d\s+\-*/().]+$/.test(safeExpr)) {
        console.warn("InteractionEngine: Expression contains disallowed characters:", safeExpr);
        return null;
      }

      // Evaluate using Function constructor (safer than eval, no access to scope)
      const fn = new Function("return (" + safeExpr + ");");
      return fn();
    } catch (err) {
      console.warn("InteractionEngine: Expression evaluation error:", err.message);
      return null;
    }
  }

  /**
   * Check if any interactions are defined for a given field.
   */
  hasInteractionsFor(fieldName) {
    return !!(this._triggerMap[fieldName] && this._triggerMap[fieldName].length > 0);
  }

  /**
   * Get all fields that are targets of visibility interactions (for initial hidden state).
   * Returns { fieldName: defaultVisible } map.
   */
  getVisibilityDefaults(allValues) {
    const defaults = {};
    this.interactions.forEach((interaction) => {
      if (interaction.type === "visibility") {
        const targets = this._getTargetFields(interaction);
        const action = interaction.action || {};
        const triggerField = interaction.trigger && interaction.trigger.field;
        const triggerValue = allValues[triggerField];
        const conditionMet = this._evaluateCondition(action.condition, triggerValue, action.value, allValues);
        const shouldShow = action.show !== false ? conditionMet : !conditionMet;

        targets.forEach((t) => {
          defaults[t] = shouldShow;
        });
      }
    });
    return defaults;
  }

  /**
   * Get all fields that are targets of conditional_enable interactions (for initial disabled state).
   * Returns { fieldName: isDisabled } map.
   */
  getDisabledDefaults(allValues) {
    const defaults = {};
    this.interactions.forEach((interaction) => {
      if (interaction.type === "conditional_enable") {
        const targets = this._getTargetFields(interaction);
        const action = interaction.action || {};
        const triggerField = interaction.trigger && interaction.trigger.field;
        const triggerValue = allValues[triggerField];
        const conditionMet = this._evaluateCondition(action.condition, triggerValue, action.value, allValues);
        const shouldEnable = action.enable !== false ? conditionMet : !conditionMet;

        targets.forEach((t) => {
          defaults[t] = !shouldEnable;
        });
      }
    });
    return defaults;
  }

  /**
   * Cleanup
   */
  destroy() {
    this._triggerMap = {};
    this.interactions = [];
    this.callbacks = {};
  }
}

export default InteractionEngine;
